import React, { useEffect, useState } from 'react'
import Switch from 'react-switch'
import { Col, FormGroup, Row, Alert, Input } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { useFormUtils } from 'views/shared/useFormUtils'
import PropTypes from 'prop-types'
import { isNotNumericDecimal } from 'context/Utilities'

const YogurtNutritionReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({
    fkCategory: item.fkCategory,
    fkSubCategory: item.fkSubCategory,
    VitaminD: item.VitaminD,
    VitaminDCertification: item.VitaminDCertification,
    Sugar: item.Sugar,
    SugarCertification: item.SugarCertification,
    SweetenerCertification: item.SweetenerCertification,
  })
  const methods = useFormContext()
  const { onChangeReviewer, setSwitchReviewer, asterisk } = useFormUtils()
  useEffect(() => {
    if (item) {
      const {
        VitaminD,
        VitaminDCertification,
        Sugar,
        SugarCertification,
        SweetenerCertification,
      } = item
      setPackaging({
        VitaminD,
        VitaminDCertification,
        Sugar,
        SugarCertification,
        SweetenerCertification,
      })
    }
    // eslint-disable-next-line
  }, [item])

  return (
    <Row>
      <Col xs={6}>
        <FormGroup>
          <p>
            <strong>Vitamin D mcg per 8 ounces {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            placeholder={'Min 2.67mcg per 8 ounces'}
            defaultValue={packaging.VitaminD}
            name="VitaminD"
            control={methods.control}
            rules={{
              required: {
                value: true,
                message: '⚠ Please enter Vitamin D mcg per 8 ounces.',
              },
              maxLength: {
                value: 6,
                message: '⚠ Vitamin D mcg per 8 ounces exceeds 6 characters.',
              },
              min: {
                value: 2.67,
                message:
                  '⚠ Vitamin D mcg per 8 ounces is below the required 2.67mcg minimum.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            onChange={([event]) => {
              onChangeReviewer(item, event)
              return event
            }}
            disabled={isDisabled}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="VitaminD"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <FormGroup>
          <p>
            <strong>Added Sugar (g) per 8 ounces {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            placeholder={'Max 16g added sugar per 8 ounces'}
            defaultValue={packaging.Sugar}
            name="Sugar"
            control={methods.control}
            rules={{
              required: {
                value: true,
                message: '⚠ Please enter Added Sugar (g) per 8 ounces.',
              },
              maxLength: {
                value: 6,
                message: '⚠ Added Sugar (g) per 8 ounces exceeds 6 characters.',
              },
              max: {
                value: 16,
                message:
                  '⚠ Added Sugar (g) per 8 ounces exceeds the maximum allowed.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            onChange={([event]) => {
              onChangeReviewer(item, event)
              return event
            }}
            disabled={isDisabled}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Sugar"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={6}>
        <FormGroup>
          <p>
            <strong>
              I certify that this product contains no less than 2.67mcg
              Vitamin D per 8 ounces. {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="VitaminDCertification"
            id="VitaminDCertification"
            defaultValue={packaging.VitaminDCertification}
            onChange={([checked, event, id]) => {
              setSwitchReviewer(item, checked, event, id)
              return checked
            }}
            control={methods.control}
            checked={packaging.VitaminDCertification}
            rules={{
              validate: VitaminDCertification =>
                VitaminDCertification === true,
            }}
            disabled={isDisabled}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="VitaminDCertification"
              message={
                '⚠ You must certify that this product contains no less than 2.67mcg Vitamin D per 8 ounces.'
              }
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <FormGroup>
          <p>
            <strong>
              I certify that this product contains no more than the maximum of
              16g total added sugar per 8 ounces. {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="SugarCertification"
            id="SugarCertification"
            defaultValue={packaging.SugarCertification}
            onChange={([checked, event, id]) => {
              setSwitchReviewer(item, checked, event, id)
              return checked
            }}
            control={methods.control}
            checked={packaging.SugarCertification}
            rules={{
              validate: SugarCertification => SugarCertification === true,
            }}
            disabled={isDisabled}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="SugarCertification"
              message={
                '⚠ You must certify that this product contains no more than the maximum of 16g total added sugar per 8 ounces.'
              }
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <FormGroup>
          <p>
            <strong>
              I certify that this product does not contain artificial
              sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.{' '}
              {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="SweetenerCertification"
            id="SweetenerCertification"
            defaultValue={packaging.SweetenerCertification}
            onChange={([checked, event, id]) => {
              setSwitchReviewer(item, checked, event, id)
              return checked
            }}
            control={methods.control}
            checked={packaging.SweetenerCertification}
            rules={{
              validate: SweetenerCertification =>
                SweetenerCertification === true,
            }}
            disabled={isDisabled}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="SweetenerCertification"
              message={
                '⚠ You must certify that this product does not contain artificial sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.'
              }
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
    </Row>
  )
}

YogurtNutritionReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default YogurtNutritionReview
